// src/Components/GoogleMap/MapOverlays.js

import React from 'react';
import { createPortal } from 'react-dom';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

export default function MapOverlays({
  isSaving,
  spinnerColor,
  tooltip,
  selectedMarker,
  activeTool,
  portalTarget, // AMENDED: Receiving the .gm-style target
}) {
  const isInstruction = typeof tooltip === 'string';

  const hasTooltipContent =
    !isInstruction &&
    tooltip &&
    tooltip.content &&
    (tooltip.content.image || tooltip.content.body);

  // AMENDED: Because we are now rendering directly inside the Google Maps Canvas (.gm-style)
  // via a portal, we calculate Map-Relative Coordinates.
  let adjustedX = tooltip?.x || 0;
  let adjustedY = tooltip?.y || 0;

  if (hasTooltipContent && portalTarget) {
    const rect = portalTarget.getBoundingClientRect();
    // Subtracting the container's bounds translates Viewport Coordinates -> Canvas Coordinates
    adjustedX = tooltip.x - rect.left;
    adjustedY = tooltip.y - rect.top;
  }

  const content = (
    <>
      {/* Saving Spinner */}
      <Backdrop
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          zIndex: 100002, // AMENDED: Elevated to Processing Overlay Tier
          position: 'absolute', // Absolute locks it perfectly inside .gm-style bounds
          pointerEvents: isSaving ? 'auto' : 'none',
        }}
        open={isSaving}>
        <CircularProgress sx={{ color: spinnerColor }} />
      </Backdrop>

      {/* Instruction Toast */}
      {isInstruction && (
        <Paper
          elevation={4}
          sx={{
            position: 'absolute', // Absolute makes it survive the fullscreen transition
            bottom: 30,
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '8px 16px',
            zIndex: 100002, // AMENDED: Elevated to Processing Overlay Tier
            pointerEvents: 'none',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: '#fff',
            borderRadius: '20px',
            fontSize: '0.85rem',
            fontWeight: 500,
          }}>
          {tooltip}
        </Paper>
      )}

      {/* Standard Mouse Tooltip */}
      {hasTooltipContent && (
        <Paper
          elevation={4}
          sx={{
            position: 'absolute', // AMENDED: Shifted from 'fixed' to 'absolute' to survive Fullscreen CSS transforms
            left: adjustedX + 15,
            top: adjustedY + 15,
            padding: '6px',
            zIndex: 100002, // AMENDED: Elevated to Processing Overlay Tier
            pointerEvents: 'none',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid #ddd',
            borderRadius: '4px',
            maxWidth: '220px',
            display: 'flex',
            flexDirection: 'column',
          }}>
          {tooltip.content.image && (
            <Box
              component='img'
              src={tooltip.content.image}
              sx={{
                width: '200px',
                height: '200px',
                borderRadius: '3px',
                mb: tooltip.content.body ? 0.5 : 0,
                objectFit: 'cover',
                pointerEvents: 'none',
              }}
            />
          )}
          {tooltip.content.body && (
            <Typography
              variant='body2'
              sx={{
                color: '#333',
                fontSize: '0.75rem',
                lineHeight: 1.3,
                display: '-webkit-box',
                overflow: 'hidden',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 3,
                pointerEvents: 'none',
              }}>
              {tooltip.content.body}
            </Typography>
          )}
        </Paper>
      )}
    </>
  );

  return portalTarget ? createPortal(content, portalTarget) : content;
}
